import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Search, LocationOn } from '@mui/icons-material';
import { SchoolData, LEVEL_COLORS, getSchoolLevel, NearestNeighborResult, CoastalResult } from './types';

interface TableViewProps {
  filteredSchools: SchoolData[];
  nearestNeighborMap?: Map<string, NearestNeighborResult>;
  coastalDistanceMap?: Map<string, CoastalResult>;
}

type SortField =
  | 'school_name'
  | 'school_level'
  | 'school_type'
  | 'district'
  | 'current_enrollment'
  | 'male_students'
  | 'female_students'
  | 'total_teachers'
  | 'nearest_distance'
  | 'coastal_distance';

type SortDirection = 'asc' | 'desc';

const TableView: React.FC<TableViewProps> = ({ filteredSchools, nearestNeighborMap, coastalDistanceMap }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('school_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const displayData = useMemo(() => {
    // Filter by search query
    const query = searchQuery.toLowerCase().trim();
    let result = filteredSchools;
    if (query) {
      result = result.filter(
        (s) =>
          s.school_name.toLowerCase().includes(query) ||
          (s.school_reg_number && s.school_reg_number.toLowerCase().includes(query))
      );
    }

    // Sort
    const sorted = [...result].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'school_name':
          aVal = (a.school_name || '').toLowerCase();
          bVal = (b.school_name || '').toLowerCase();
          break;
        case 'school_level':
          aVal = getSchoolLevel(a).toLowerCase();
          bVal = getSchoolLevel(b).toLowerCase();
          break;
        case 'school_type':
          aVal = (a.school_type || '').toLowerCase();
          bVal = (b.school_type || '').toLowerCase();
          break;
        case 'district':
          aVal = (a.district?.district_name || '').toLowerCase();
          bVal = (b.district?.district_name || '').toLowerCase();
          break;
        case 'current_enrollment':
          aVal = a.current_enrollment || 0;
          bVal = b.current_enrollment || 0;
          break;
        case 'male_students':
          aVal = a.male_students || 0;
          bVal = b.male_students || 0;
          break;
        case 'female_students':
          aVal = a.female_students || 0;
          bVal = b.female_students || 0;
          break;
        case 'total_teachers':
          aVal = a.total_teachers || 0;
          bVal = b.total_teachers || 0;
          break;
        case 'nearest_distance':
          aVal = nearestNeighborMap?.get(a.school_id)?.distanceKm ?? 999;
          bVal = nearestNeighborMap?.get(b.school_id)?.distanceKm ?? 999;
          break;
        case 'coastal_distance':
          aVal = coastalDistanceMap?.get(a.school_id)?.distanceToCoastKm ?? 999;
          bVal = coastalDistanceMap?.get(b.school_id)?.distanceToCoastKm ?? 999;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredSchools, searchQuery, sortField, sortDirection, nearestNeighborMap, coastalDistanceMap]);

  const computePTR = (enrollment: number, teachers: number): string => {
    if (!teachers || teachers === 0) return '-';
    return Math.round(enrollment / teachers).toString();
  };

  const hasGPS = (school: SchoolData): boolean => {
    return school.latitude !== null && school.longitude !== null && school.latitude !== 0 && school.longitude !== 0;
  };

  return (
    <Box>
      {/* Search field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by school name or EMIS number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: '100%', sm: 400 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Showing {displayData.length.toLocaleString()} of {filteredSchools.length.toLocaleString()} schools
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', width: 50 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 200 }}>
                <TableSortLabel
                  active={sortField === 'school_name'}
                  direction={sortField === 'school_name' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('school_name')}
                >
                  School Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 100 }}>EMIS</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 120 }}>
                <TableSortLabel
                  active={sortField === 'school_level'}
                  direction={sortField === 'school_level' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('school_level')}
                >
                  School Level
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 100 }}>
                <TableSortLabel
                  active={sortField === 'school_type'}
                  direction={sortField === 'school_type' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('school_type')}
                >
                  School Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 130 }}>
                <TableSortLabel
                  active={sortField === 'district'}
                  direction={sortField === 'district' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('district')}
                >
                  District
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 90 }}>
                <TableSortLabel
                  active={sortField === 'current_enrollment'}
                  direction={sortField === 'current_enrollment' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('current_enrollment')}
                >
                  Students
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 70 }}>
                <TableSortLabel
                  active={sortField === 'male_students'}
                  direction={sortField === 'male_students' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('male_students')}
                >
                  Male
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 70 }}>
                <TableSortLabel
                  active={sortField === 'female_students'}
                  direction={sortField === 'female_students' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('female_students')}
                >
                  Female
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 80 }}>
                <TableSortLabel
                  active={sortField === 'total_teachers'}
                  direction={sortField === 'total_teachers' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('total_teachers')}
                >
                  Teachers
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 60 }}>PTR</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 50 }}>GPS</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                <TableSortLabel
                  active={sortField === 'nearest_distance'}
                  direction={sortField === 'nearest_distance' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('nearest_distance')}
                >
                  Nearest (km)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                <TableSortLabel
                  active={sortField === 'coastal_distance'}
                  direction={sortField === 'coastal_distance' ? sortDirection : 'asc'}
                  onClick={() => handleSortClick('coastal_distance')}
                >
                  Coast (km)
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((school, index) => {
              const level = getSchoolLevel(school);
              const levelColor = LEVEL_COLORS[level] || LEVEL_COLORS.Unknown;
              return (
                <TableRow
                  key={school.school_id || index}
                  hover
                  sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}
                >
                  <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {school.school_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace" fontSize={12}>
                      {school.school_reg_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={level}
                      size="small"
                      sx={{
                        bgcolor: `${levelColor}18`,
                        color: levelColor,
                        fontWeight: 600,
                        fontSize: 11,
                        height: 24,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {(school.school_type || '-').toLowerCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {school.district?.district_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {(school.current_enrollment || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="info.main">
                      {(school.male_students || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="secondary.main">
                      {(school.female_students || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {(school.total_teachers || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {computePTR(school.current_enrollment || 0, school.total_teachers || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {hasGPS(school) ? (
                      <LocationOn sx={{ fontSize: 18, color: '#4CAF50' }} />
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  {(() => {
                    const nn = nearestNeighborMap?.get(school.school_id);
                    return (
                      <TableCell align="right">
                        {nn ? (
                          <Tooltip title={`Nearest: ${nn.nearestSchoolName}`} arrow>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                color: nn.distanceKm < 1 ? '#388E3C' : nn.distanceKm > 5 ? '#D32F2F' : 'inherit',
                                fontWeight: nn.distanceKm > 5 ? 600 : 400,
                              }}
                            >
                              {nn.distanceKm.toFixed(1)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                    );
                  })()}
                  {(() => {
                    const cd = coastalDistanceMap?.get(school.school_id);
                    return (
                      <TableCell align="right">
                        {cd ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: cd.isCoastalZone ? 600 : 400,
                              color: cd.isCoastalZone ? '#0288D1' : 'inherit',
                            }}
                          >
                            {cd.distanceToCoastKm.toFixed(1)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                    );
                  })()}
                </TableRow>
              );
            })}
            {displayData.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    No schools found matching your search criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TableView;
